<template>
    <section class="paillasse-patient-analyse" :aria-busy="loading ? 'true' : 'false'" :aria-labelledby="'analyse-patient-' + _uid">
        <header class="paillasse-patient-analyse-entete">
            <div class="paillasse-patient-analyse-identite">
                <h4 :id="'analyse-patient-' + _uid"><span v-if="numero" class="paillasse-analyse-numero">{{ numero }}.</span> {{ ligne.analyse }}</h4>
                <small>{{ (ligne.echantillons || []).join(', ') }}<template v-if="ligne.technique_utilisee"> · {{ ligne.technique_utilisee }}</template></small>
            </div>
            <span v-if="saved && !isDirty()" class="paillasse-resultat-enregistre" role="status"><i class="fa fa-check" aria-hidden="true"></i> Enregistré</span>
        </header>
        <p v-if="loading" class="paillasse-saisie-aide" role="status">Chargement des champs…</p>
        <div v-else-if="error" class="alert alert-danger" role="alert">
            {{ error }} <button type="button" class="btn btn-outline-primary btn-sm" @click="load">Réessayer</button>
        </div>
        <div v-else-if="!models.length" class="alert alert-warning" role="alert">
            Aucun modèle de résultat n’est disponible pour cette analyse.
            <button type="button" class="btn btn-outline-primary btn-sm" @click="$emit('create-model', ligne)">Créer le modèle</button>
        </div>
        <template v-else>
            <label v-if="models.length > 1 && !result" class="paillasse-choix-modele" :for="'modele-patient-' + _uid">
                Modèle de résultat
                <select :id="'modele-patient-' + _uid" :value="selectedModelId" :disabled="saved || saving" @change="chooseModel($event)">
                    <option value="">Choisir le modèle à utiliser</option>
                    <option v-for="model in models" :key="model.id" :value="model.id">{{ model.name }}</option>
                </select>
            </label>
            <ResultatsAnalysesFormTable v-if="context" :key="selectedModelId" ref="form" container="modal" :entry-context="context" :batch-saving="saving" @onResultSavedSuccessfully="afterSave" />
        </template>
    </section>
</template>

<script>
import api from '@/api/parametres/_modele-analyses'
import ResultatsAnalysesFormTable from '@/views/analyse/resultats-analyses/saisie-resultats/components/ResultatsAnalysesFormTable'

export default {
    name: 'SaisieAnalysePatient',
    components: { ResultatsAnalysesFormTable },
    props: { ligne: { type: Object, required: true }, numero: { type: Number, default: null }, saving: { type: Boolean, default: false } },
    data() {
        return { loading: false, error: '', models: [], result: null, selectedModelId: '', saved: false, sequence: 0 }
    },
    computed: {
        context() {
            const model = this.models.find(item => String(item.id) === String(this.selectedModelId))
            if (!model) return null
            const ligne = this.ligne
            return {
                model, result: this.result,
                patient: {
                    patient_id: ligne.patient_id, patient_fullname: ligne.patient, patient_age: ligne.age, patient_gender: ligne.sexe, patient_phone: ligne.telephone,
                    doctor_fullname: ligne.prescripteur, clinical_inquiries: ligne.renseignement_clinique, methodology_applied: ligne.technique_utilisee,
                    bulletin_analyse_id: ligne.bulletin_id, demande_analyse_id: ligne.demande_id
                }
            }
        }
    },
    mounted() { this.load() },
    beforeDestroy() { this.sequence++ },
    methods: {
        async load() {
            const sequence = ++this.sequence
            this.loading = true
            this.error = ''
            try {
                // Lecture seule et sans mutations Vuex : tous les formulaires peuvent
                // rester ouverts sans partager modèles, valeurs ou identité du patient.
                const [models, response] = await Promise.all([
                    api.getModelsAnalyses({ analyse_id: this.ligne.analyse_id, status: 'active' }),
                    this.$store.dispatch('getResultsByAnalyseIdBulletinId', { analyseId: this.ligne.analyse_id, bulletinId: this.ligne.bulletin_id })
                ])
                if (sequence !== this.sequence) return
                if (!Array.isArray(models)) throw new Error('Réponse de modèles invalide')
                this.models = models
                this.result = response && response.data && response.data.id ? response.data : null
                if (this.result) {
                    const model = models.find(item => String(item.id) === String(this.result.model_analyse_id))
                    if (!model) throw new Error('Le modèle du résultat enregistré n’est plus disponible. Faites vérifier son activation avant de modifier ce résultat.')
                    this.selectedModelId = model.id
                    this.saved = true
                } else {
                    this.selectedModelId = models.length === 1 ? models[0].id : ''
                }
            } catch (error) {
                if (sequence === this.sequence) this.error = (error.response && error.response.data && error.response.data.message) || error.message || 'Impossible de charger les champs de cette analyse.'
            } finally {
                if (sequence === this.sequence) this.loading = false
            }
        },
        isBusy() {
            const form = this.$refs.form
            return !!(form && (form.isLoading || (form.$refs.documents && form.$refs.documents.uploading)))
        },
        isDirty() {
            const form = this.$refs.form
            return !!(form && (form.hasUnsavedChanges || (!form.saved_resultats && form.hasResultInput())))
        },
        shouldSave() { return this.isDirty() },
        validate() { return !!this.$refs.form && this.$refs.form.validateBeforeSave() },
        save() { return this.$refs.form.saveResults() },
        chooseModel(event) {
            const form = this.$refs.form
            // Après sauvegarde, conserver le modèle lié au résultat et son identifiant.
            if (this.saving || this.isBusy() || (form && form.saved_resultats && form.saved_resultats.id) || (this.isDirty() && !window.confirm('Changer de modèle et abandonner les valeurs non enregistrées de cette analyse ?'))) {
                event.target.value = this.selectedModelId
                return
            }
            this.selectedModelId = event.target.value
        },
        afterSave() {
            this.saved = true
            this.$emit('saved', this.ligne)
        }
    }
}
</script>
